/**
 * U1.4. The profile form — the first screen a new person sees
 * (`screens/Onboarding.tsx`) and the "You" section of Account
 * (`AccountSheet.tsx`). One component, so the two cannot drift.
 *
 * Controlled: the caller owns the `Draft` (`lib/onboarding.ts`) and decides
 * when it is written. The form itself writes nothing, which is what lets
 * onboarding's "Later" and Account's "Save" be different buttons over the
 * same fields. Every decision — what a draft becomes, which airports parse —
 * is in `onboarding.ts`; this file draws controls.
 *
 * The primitives are the app's sixteen. A switch is not one of them (D5: a
 * screen that wants a seventeenth is wrong), so the two on/off questions are
 * a `Checkbox` in a row whose words are tappable too.
 */
import { Suspense, lazy, useId, useMemo, useState, type ReactNode } from 'react'
import { MapPin, X } from 'lucide-react'
import { Button, Checkbox, Chip, Field, Sheet } from '../ui'
import { hasHolidays } from '../lib/holidays'
import { useProfile } from '../lib/profile'
import { homeName, useRegionsNow } from '../lib/regions'
import {
  COUNTRIES, S_LINES, U_LINES, chosenIn, districtGroups, parseAirports, toggled, type Draft,
} from '../lib/onboarding'
import './ProfileForm.css'

// Leaflet and its stylesheet only when someone opens the map.
const PinMap = lazy(() => import('./PinMap'))

type SetFn = <K extends keyof Draft>(key: K, value: Draft[K]) => void

/** A yes/no question as a row: words on the left, a tick on the right, the
 *  whole row answering. The tick is the control for a screen reader; the text
 *  is a second, larger target for a finger and is `aria-hidden` from the
 *  tab order because it would only repeat the tick. */
export function Toggle({ label, meta, on, onChange }: {
  label: string
  meta?: string
  on: boolean
  onChange: (on: boolean) => void
}) {
  const id = useId()
  return (
    <div className="pf__toggle">
      <button type="button" className="pf__toggle-text" tabIndex={-1} aria-hidden="true"
              onClick={() => onChange(!on)}>
        <span className="pf__toggle-label" id={id}>{label}</span>
        {meta && <span className="pf__toggle-meta">{meta}</span>}
      </button>
      <Checkbox checked={on} onChange={onChange} shape="square" aria-labelledby={id} />
    </div>
  )
}

/** A question with a heading that is not a single control's label (chips). */
export function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const id = useId()
  return (
    <div className="pf__group" role="group" aria-labelledby={id}>
      <h3 className="pf__q" id={id}>{title}</h3>
      {hint && <p className="pf__hint">{hint}</p>}
      {children}
    </div>
  )
}

export function ProfileForm({ draft, onChange, nameError }: {
  draft: Draft
  onChange: (next: Draft) => void
  /** Shown under the name — Onboarding sets it when Done is pressed with
   *  none. Account never does: a name can be cleared there. */
  nameError?: string
}) {
  const set: SetFn = (key, value) => onChange({ ...draft, [key]: value })
  return (
    <div className="pf">
      <Field label="What should Kiez call you?" error={nameError}>
        <Field.Input value={draft.name} autoComplete="given-name" enterKeyHint="next"
                     onChange={e => set('name', e.currentTarget.value)} />
      </Field>

      <Where draft={draft} set={set} onChange={onChange} />

      <Toggle label="Only events I can follow in English"
              meta="Leaves out the ones that are German-only."
              on={draft.englishOnly} onChange={v => set('englishOnly', v)} />

      <Group title="Your lines" hint="City news on a line you ride comes first.">
        <div className="pf__chips">
          {[...U_LINES, ...S_LINES].map(l => (
            <Chip key={l} on={draft.lines.includes(l)} onClick={() => set('lines', toggled(draft.lines, l))}>{l}</Chip>
          ))}
        </div>
      </Group>

      <SecondHome draft={draft} set={set} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Where in the home region
// ---------------------------------------------------------------------------

function Where({ draft, set, onChange }: { draft: Draft; set: SetFn; onChange: (next: Draft) => void }) {
  const groups = useMemo(districtGroups, [])
  // What the home region is called (C2.3): this form writes the home place.
  const home = homeName(useRegionsNow(), useProfile())
  // The Bezirk whose Ortsteile are showing. Starts on the first one the person
  // has already chosen from, so Account opens on their own neighbourhood.
  const [open, setOpen] = useState<string | null>(() =>
    groups.find(g => chosenIn(g, draft.districts) > 0)?.bezirk ?? null)
  const [mapOpen, setMapOpen] = useState(false)
  // The pin as it is being placed; written to the draft only by "Use this spot".
  const [trial, setTrial] = useState<{ lat: number; lng: number } | null>(null)
  const pin = draft.lat != null && draft.lng != null ? { lat: draft.lat, lng: draft.lng } : null
  const shown = groups.find(g => g.bezirk === open)

  return (
    <Group title={`Where in ${home}?`}
           hint="Pick your Bezirk, then the neighbourhoods you live in or near. Optional.">
      <div className="pf__chips">
        {groups.map(g => (
          <Chip key={g.bezirk} on={open === g.bezirk} count={chosenIn(g, draft.districts)}
                aria-expanded={open === g.bezirk}
                onClick={() => setOpen(open === g.bezirk ? null : g.bezirk)}>
            {g.bezirk}
          </Chip>
        ))}
      </div>

      {shown && (
        <div className="pf__ortsteile">
          <p className="pf__sub">{shown.bezirk}</p>
          <div className="pf__chips">
            {shown.ortsteile.map(o => (
              <Chip key={o} on={draft.districts.includes(o)}
                    onClick={() => set('districts', toggled(draft.districts, o))}>
                {o}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {draft.districts.length > 0 && (
        <p className="pf__chosen">
          <span className="pf__chosen-k">Chosen</span> {draft.districts.join(', ')}
        </p>
      )}

      <div className="pf__pin">
        {pin ? (
          <>
            <p className="pf__pinline"><MapPin size={16} aria-hidden="true" /> Pin set. Near me works.</p>
            <div className="pf__actions">
              <Button variant="secondary" size="sm" onClick={() => { setTrial(pin); setMapOpen(true) }}>Move pin</Button>
              <Button variant="quiet" size="sm" onClick={() => onChange({ ...draft, lat: null, lng: null })}>Remove pin</Button>
            </div>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" icon={<MapPin size={16} />}
                    onClick={() => { setTrial(null); setMapOpen(true) }}>
              Drop a pin
            </Button>
            <p className="pf__hint">A pin turns on Near me. Neighbourhoods alone do not: the middle of one is not your street.</p>
          </>
        )}
      </div>

      <Sheet open={mapOpen} onOpenChange={setMapOpen} title="Drop a pin" snap
             actions={
               <>
                 <Button variant="quiet" onClick={() => setMapOpen(false)}>Cancel</Button>
                 <Button disabled={!trial} onClick={() => {
                   if (!trial) return
                   onChange({ ...draft, lat: trial.lat, lng: trial.lng })
                   setMapOpen(false)
                 }}>
                   Use this spot
                 </Button>
               </>
             }>
        <p className="pf__hint pf__hint--sheet">Tap the map where you live. Only you see it; it is used for Near me.</p>
        <Suspense fallback={<div className="pmap pmap--loading" />}>
          <PinMap value={trial} onChange={(lat, lng) => setTrial({ lat, lng })} />
        </Suspense>
      </Sheet>
    </Group>
  )
}

// ---------------------------------------------------------------------------
// A second home
// ---------------------------------------------------------------------------

function SecondHome({ draft, set }: { draft: Draft; set: SetFn }) {
  const [typed, setTyped] = useState('')
  const [note, setNote] = useState<string | undefined>()

  function add() {
    const { codes, rejected } = parseAirports(typed)
    if (codes.length) set('airports', [...draft.airports, ...codes.filter(c => !draft.airports.includes(c))])
    setNote(rejected.length ? `${rejected.join(', ')} ${rejected.length > 1 ? 'are' : 'is'} not an airport code (three letters, like DUB).` : undefined)
    setTyped(rejected.length ? rejected.join(' ') : '')
  }

  const country = draft.secondCountry
  const calendarNote = country && !hasHolidays(country)
    ? 'No holiday calendar for this country yet. Stays and flights still work.'
    : undefined

  return (
    <div className="pf__group">
      <Toggle label="A second home?"
              meta="Somewhere else you spend time. Year plans stays and flights there."
              on={draft.second} onChange={v => set('second', v)} />
      {draft.second && (
        <div className="pf__second">
          <Field label="What do you call it?">
            <Field.Input value={draft.secondLabel} placeholder="Ireland, Lisbon, the cabin"
                         onChange={e => set('secondLabel', e.currentTarget.value)} />
          </Field>
          <Field label="Country" hint={calendarNote}>
            <Field.Select value={country || null} placeholder="Choose a country"
                          options={COUNTRIES.map(c => ({ value: c.code, label: c.name }))}
                          onChange={v => set('secondCountry', v)} />
          </Field>
          <Group title="Airports" hint="The ones that count as going home. The first is the one flights default to.">
            {draft.airports.length > 0 && (
              <div className="pf__chips">
                {draft.airports.map(a => (
                  <Chip key={a} on aria-label={`Remove ${a}`}
                        onClick={() => set('airports', draft.airports.filter(x => x !== a))}>
                    {a} <X size={14} aria-hidden="true" />
                  </Chip>
                ))}
              </div>
            )}
            <form className="pf__addrow" onSubmit={e => { e.preventDefault(); add() }}>
              <Field label="Add an airport" error={note}>
                <Field.Input value={typed} placeholder="DUB" autoCapitalize="characters" autoCorrect="off"
                             maxLength={32} enterKeyHint="done"
                             onChange={e => { setTyped(e.currentTarget.value); setNote(undefined) }} />
              </Field>
              <Button type="submit" variant="secondary" disabled={!typed.trim()}>Add</Button>
            </form>
          </Group>
        </div>
      )}
    </div>
  )
}
